import { mountReportingSurface } from '../core/reportingSurface.js';
import { loadingState, beginRefresh, settleLoadingValues, failLoadingStates } from '../core/loadingState.js';
import { takePerformancePeriod } from '../core/performanceNavigation.js';
/**
 * ad-performance-route.js — client-saas /analytics/ad-performance
 *
 * The Advertising report, built as three disclosure layers on one page:
 *   1. A reactive KPI strip (spend, leads from ads, cost per lead, answer rate) that is
 *      complete on its own, responds to the period, to the source chips under Leads and
 *      to a chart hover, and carries attention badges only when something needs a human.
 *   2. One chart, and sections that render COLLAPSED with a summary line computed from
 *      live data, so a closed page still reads as a report.
 *   3. Full tables, a "how these numbers are counted" block and "Expand all", remembered
 *      per user, for the reader who wants everything.
 *
 * One state object (period, selected sources, open sections, hover day) drives every
 * section; chip toggles filter the per-source series client-side and never refetch.
 * Data: GET /coreAdMetrics/overlay?compare=1 (leads split by source + previous window),
 * /by-source, /conversions, /lsa, /coreAdAccount. Honest empty states — never fabricated
 * numbers. Google's conversion counts stay their own stream and never join the lead sum.
 */

import AuthController from '../services/AuthController.js';
import { inPortfolio, pickerPageHTML, initPicker } from '../services/portfolio-view.js';
import {
  PROVIDERS, connFor, statusOf, statusPill, fetchIntegrations,
  canManage, ensureStyles as ensureIntgStyles,
} from '../services/integrations-shared.js';
import { statCard } from '../core/statCard.js';
import { ensureChartStyles, dualAxisChartHTML, wireChartHover, fmtAxis } from '../services/chart-kit.js';
import { capList } from '../core/listExpand.js';
import { setLeadsHandoff, leadsHandoffScope } from '../core/routeHandoff.js';
import { guardedFetch } from '../core/navGeneration.js';
import { isDenial, denialState, failIfNotOk } from '../core/denialSurface.js';
import {
  SOURCES, SECTIONS, readView, saveView, stripFigures, trend, selectedLeads, filterSourceRows,
  campaignSummary, conversionSummary, lsaSummary, shortDate, drillSource,
} from './ad-performance-view.js';

let drillScope;
const advertisingLeads = context => setLeadsHandoff({ ...(drillScope || leadsHandoffScope()), ...context, returnTo: { hash: '#/analytics/ad-performance', label: 'Back to Advertising' } });

const apiBase = () => AuthController.getConfig?.().BASE_URL || '';
const ymd = (d) => d.toISOString().slice(0, 10);
const fmtMoney = (n) => (n == null ? '—' : '$' + Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }));
const fmtNum = (n) => Number(n || 0).toLocaleString();
const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]); // UTM values come from URLs

let stylesInjected = false;
function ensureStyles() {
  ensureChartStyles();
  if (stylesInjected) return;
  const s = document.createElement('style');
  s.id = 'm5t-adperf-styles';
  s.textContent = `
    .page-shell .adperf-note { font-size: 0.76rem; color: var(--color-text-subtle, #6C7C9A); margin: 12px 2px 0; }
    .page-shell #adperf-lsa-shortcut { margin: 0 0 16px; }
    .page-shell .adperf-lsa-jump { max-width: 100%; white-space: normal; text-align: left; font: inherit; font-size: 0.88rem; font-weight: 600; text-transform: none; color: var(--color-primary-600, #0072BA); background: var(--color-bg, #fff); border: 1px solid var(--color-border, #D4DCE8); border-radius: var(--radius-md, 8px); padding: 12px 16px; cursor: pointer; }
    .page-shell .adperf-lsa-jump:focus-visible { outline: 2px solid var(--color-primary-600, #0072BA); outline-offset: 3px; }
    .page-shell .chart-period-btn { cursor: pointer; }
    .page-shell .chart-period-btn.active { background: var(--color-primary-600, #0072BA); color: #fff; }
    .page-shell .adperf-srcwrap { overflow-x: auto; }
    .page-shell .adperf-srctable { width: 100%; border-collapse: collapse; font-size: 0.88rem; }
    .page-shell .adperf-srctable th { text-align: left; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-subtle, #6C7C9A); padding: 8px 12px; border-bottom: 1px solid var(--color-border, #D4DCE8); white-space: nowrap; }
    .page-shell .adperf-srctable td { padding: 9px 12px; border-bottom: 1px solid var(--color-surface-2, #EAF0F7); vertical-align: middle; }
    .page-shell .adperf-srctable .num { text-align: right; white-space: nowrap; }
    .page-shell .adperf-srcrow--link { cursor: pointer; transition: background 0.12s ease; }
    .page-shell .adperf-srcrow--link:hover { background: var(--color-surface, #F4F7FB); }
    .page-shell .adperf-srclink { color: inherit; text-decoration: none; font-weight: 600; }
    .page-shell .adperf-srcrow--link:hover .adperf-srclink { color: var(--color-primary-600, #0072BA); text-decoration: underline; }
    .page-shell .adperf-srcrow--link:focus-within { outline: 2px solid var(--color-primary-600, #0072BA); outline-offset: -2px; }
    html.ff-no-animations .page-shell .adperf-srcrow--link { transition: none; }
    @media (prefers-reduced-motion: reduce) { .page-shell .adperf-srcrow--link { transition: none; } }
    .page-shell .adperf-src-sub { font-size: 0.78rem; color: var(--color-text-subtle, #6C7C9A); }
    .page-shell .adperf-src-untracked { color: var(--color-text-subtle, #6C7C9A); font-style: italic; }
    .page-shell .adperf-kind { display: inline-flex; align-items: center; gap: 4px; font-size: 0.78rem; color: var(--color-text-muted, #4A5A78); }
    .page-shell .adperf-kind .material-symbols-outlined { font-size: 16px; }
    .page-shell .adperf-conn { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: 8px; font-size: 0.8rem; font-weight: 600; color: var(--color-text-muted, #4A5A78); }
    .page-shell .adperf-conn:empty { display: none; }
    .page-shell .adperf-conn a { color: var(--color-accent, #0072BA); font-weight: 600; }
    .page-shell .adperf-convsel { font: inherit; font-size: 0.8rem; font-weight: 600; color: var(--color-text-muted, #4A5A78); background: var(--color-bg, #fff); border: 1px solid var(--color-border, #D4DCE8); border-radius: 8px; padding: 4px 8px; max-width: 240px; cursor: pointer; }
    .page-shell .adperf-convsel:focus-visible { outline: 2px solid var(--color-primary-600, #0072BA); outline-offset: 1px; }

    /* KPI strip: the recap layer. Sublabels hold the context; chips and badges are the
       interaction. A day being read on the chart swaps the strip into day mode. */
    .page-shell .adperf-strip .stat-change { flex-wrap: wrap; row-gap: 6px; }
    .page-shell .adperf-strip--day .stat-value { color: var(--color-primary-700, #005E99); }
    .page-shell .adperf-strip--day .stat-trend { display: none; }
    .page-shell .adperf-chips { display: inline-flex; flex-wrap: wrap; gap: 6px; }
    .page-shell .adperf-chip { display: inline-flex; align-items: center; gap: 4px; font: inherit; font-size: 0.76rem; font-weight: 600; padding: 3px 9px; border-radius: 999px; border: 1px solid var(--color-border, #D4DCE8); background: var(--color-surface, #F4F7FB); color: var(--color-text-muted, #4A5A78); cursor: pointer; line-height: 1.3; }
    .page-shell .adperf-chip .material-symbols-outlined { font-size: 14px; }
    .page-shell .adperf-chip[aria-pressed="true"] { background: var(--color-primary-600, #0072BA); border-color: var(--color-primary-600, #0072BA); color: #fff; }
    .page-shell .adperf-chip:focus-visible { outline: 2px solid var(--color-primary-600, #0072BA); outline-offset: 2px; }
    .page-shell .adperf-badge { display: inline-flex; align-items: center; gap: 4px; font-size: 0.76rem; font-weight: 700; padding: 3px 9px; border-radius: 999px; background: var(--color-warning-bg, #FFF4D6); color: var(--color-warning-text, #7A4B00); text-decoration: none; }
    .page-shell .adperf-badge .material-symbols-outlined { font-size: 14px; }
    .page-shell .adperf-badge:hover { text-decoration: underline; }

    /* Disclosure sections: native details, summary line always computed. */
    .page-shell .adperf-tools { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin: 4px 0 10px; font-size: 0.8rem; color: var(--color-text-subtle, #6C7C9A); }
    .page-shell .adperf-tools button { font: inherit; font-size: 0.8rem; font-weight: 600; color: var(--color-accent, #0072BA); background: none; border: 0; padding: 4px 0; cursor: pointer; }
    .page-shell .adperf-tools button:focus-visible { outline: 2px solid var(--color-primary-600, #0072BA); outline-offset: 2px; }
    .page-shell details.adperf-sec { margin-bottom: 16px; }
    .page-shell details.adperf-sec[hidden] { display: none; }
    .page-shell .adperf-sec > summary { list-style: none; display: flex; align-items: center; gap: 12px; flex-wrap: wrap; cursor: pointer; padding: 18px 24px; }
    .page-shell .adperf-sec > summary::-webkit-details-marker { display: none; }
    .page-shell .adperf-sec > summary::before { content: 'chevron_right'; font-family: 'Material Symbols Outlined'; font-size: 20px; color: var(--color-text-subtle, #6C7C9A); transition: transform 0.15s ease; }
    .page-shell .adperf-sec[open] > summary::before { transform: rotate(90deg); }
    html.ff-no-animations .page-shell .adperf-sec > summary::before { transition: none; }
    .page-shell .adperf-sec > summary:focus-visible { outline: 2px solid var(--color-primary-600, #0072BA); outline-offset: -2px; border-radius: var(--radius-md, 12px); }
    .page-shell .adperf-sec > summary .card-title { margin: 0; }
    .page-shell .adperf-sec-line { font-size: 0.82rem; color: var(--color-text-muted, #4A5A78); margin-left: auto; text-align: right; }
    .page-shell .adperf-sec-line.empty { color: var(--color-text-subtle, #6C7C9A); font-style: italic; }
    .page-shell .adperf-sec > .card-body { padding-top: 0; }
    .page-shell .adperf-sec-head { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 12px; }
    .page-shell .adperf-sec-head .live-indicator { margin-left: auto; }
    .page-shell .adperf-defs dt { font-weight: 600; margin-top: 10px; }
    .page-shell .adperf-defs dd { margin: 2px 0 0; font-size: 0.86rem; color: var(--color-text-muted, #4A5A78); }
    @media (max-width: 720px) { .page-shell .adperf-sec-line { margin-left: 0; text-align: left; width: 100%; } }
  `;
  document.head.appendChild(s);
  stylesInjected = true;
}

const emptyState = (icon, msg) =>
  `<div class="m5t-empty"><span class="material-symbols-outlined" aria-hidden="true">${icon}</span><p>${msg}</p></div>`;

const PERIODS = [{ d: 7, label: '7 Days' }, { d: 30, label: '30 Days' }, { d: 90, label: '90 Days' }];
let periodDays = 30;
// Conversion scope for the Other conversions section: 'all' (every action Google
// recorded - the default, so this report matches the Google Ads UI), 'saved'
// (the selection saved on the integration page), or a single action id.
// View-only: picking here never writes conversion_action_ids back.
let convScope = 'all';

// ── page state ────────────────────────────────────────────────────────────────
// One object every section renders from. `view` is the remembered part (sources,
// open sections, expand-all); `data` is the last load; `range` is the window.
const state = { view: null, data: null, range: {}, hoverRow: null, chartCleanup: null };

const SECTION_TITLES = {
  campaigns: { icon: 'campaign', title: 'Results by campaign & source' },
  conversions: { icon: 'flag', title: 'Other conversions' },
  lsa: { icon: 'home_repair_service', title: 'Local Services' },
  counting: { icon: 'info', title: 'How these numbers are counted' },
};
function sectionHTML(key, bodyHTML, { id } = {}) {
  const t = SECTION_TITLES[key];
  return `
      <details class="dashboard-card adperf-sec" data-sec="${key}"${id ? ` id="${id}"` : ''}>
        <summary>
          <h2 class="card-title"><span class="material-symbols-outlined" aria-hidden="true">${t.icon}</span> ${t.title}</h2>
          <span class="adperf-sec-line" id="adperf-sum-${key}"></span>
        </summary>
        <div class="card-body">${bodyHTML}</div>
      </details>`;
}

export function render() {
  periodDays = takePerformancePeriod('/analytics/ad-performance', periodDays);
  if (inPortfolio()) {
    return pickerPageHTML({
      section: 'ad-performance', title: 'Advertising',
      description: 'Pick a client to view their spend and lead performance.',
    });
  }
  ensureStyles();
  return `
    <div class="page-shell" data-section="ad-performance">
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">Advertising</h1>
          <p class="page-description">Ad spend, captured leads and Local Services call activity.</p>
          <div class="adperf-conn" id="adperf-conn"></div>
        </div>
        <div class="chart-controls" id="adperf-periods">
          ${PERIODS.map((p) => `<button class="chart-period-btn${p.d === periodDays ? ' active' : ''}" type="button" data-days="${p.d}">${p.label}</button>`).join('')}
        </div>
      </div>

      <div class="stats-grid adperf-strip" id="adperf-stats">
        ${statCard({ loading: true, icon: 'payments', label: 'Ad Spend', valueId: 'adperf-spend', trendId: 'adperf-spend-trend', subId: 'adperf-spend-sub', href: '#/ad-accounts' })}
        ${statCard({ loading: true, icon: 'person_add', label: 'Inquiries & calls', valueId: 'adperf-leads', trendId: 'adperf-leads-trend', subId: 'adperf-leads-sub' })}
        ${statCard({ loading: true, icon: 'request_quote', label: 'Cost / inquiry or call', valueId: 'adperf-cpl', trendId: 'adperf-cpl-trend', subId: 'adperf-cpl-sub' })}
        ${statCard({ loading: true, icon: 'call', label: 'Answer Rate', valueId: 'adperf-answer', subId: 'adperf-answer-sub' })}
      </div>

      <p class="adperf-note" id="adperf-lsa-shortcut" hidden></p>
      <div class="dashboard-card">
        <div class="card-header">
          <h2 class="card-title"><span class="material-symbols-outlined" aria-hidden="true">insights</span> Spend & incoming activity</h2>
          <span class="live-indicator" id="adperf-range"></span>
        </div>
        <div class="card-body" id="adperf-body">
          ${loadingState('chart', 'Loading advertising results')}
        </div>
      </div>
      <p class="adperf-note">Explore daily spend and incoming activity. Select a day to see its totals above.</p>

      <div class="adperf-tools">
        <span>Details</span>
        <button type="button" id="adperf-expand">Expand all</button>
      </div>

      ${sectionHTML('campaigns', `<div id="adperf-sources">${loadingState('list', 'Loading campaigns')}</div>`)}
      ${sectionHTML('conversions', `
          <div class="adperf-sec-head">
            <select id="adperf-conv-scope" class="adperf-convsel" aria-label="Conversion actions"></select>
            <span class="live-indicator" id="adperf-conv-cpc"></span>
          </div>
          <div id="adperf-conv-body"></div>`, { id: 'adperf-conv-card' })}
      ${sectionHTML('lsa', `<div id="adperf-lsa-body"></div>`, { id: 'adperf-lsa-card' })}
      ${sectionHTML('counting', `<div id="adperf-counting"></div>`)}
    </div>`;
}

export async function init() {
  drillScope = leadsHandoffScope();
  state.range = {}; state.hoverRow = null; state.data = null;
  if (inPortfolio()) {
    const fmtN = (n) => Number(n || 0).toLocaleString();
    return initPicker(apiBase(), [
      { h: 'Spend', td: (r) => (r.ad ? `$${fmtN(r.ad.spend)}` : '—') },
      { h: 'Clicks', td: (r) => (r.ad ? fmtN(r.ad.clicks) : '—') },
      { h: 'Leads', td: (r) => fmtN(r.ad?.leads ?? r.leads.window_ads ?? r.leads.window ?? r.leads.total) },
      { h: 'CPL', td: (r) => (r.ad?.cost_per_lead != null ? `$${fmtN(r.ad.cost_per_lead)}` : '—') },
    ], { emptyMsg: 'No client orgs yet. Connect a client ad account to see spend here.' });
  }
  mountReportingSurface('advertising');
  state.view = readView();

  document.getElementById('adperf-periods')?.addEventListener('click', async (e) => {
    const btn = e.target.closest('.chart-period-btn');
    if (!btn) return;
    periodDays = Number(btn.dataset.days) || 30;
    document.querySelectorAll('#adperf-periods .chart-period-btn').forEach((b) => b.classList.toggle('active', b === btn));
    await load();
  });

  // Source chips under Leads: toggles, delegated on the strip (the chips re-render).
  // Every chip off is a dead page, so the last one on cannot be turned off.
  document.getElementById('adperf-stats')?.addEventListener('click', (e) => {
    const chip = e.target.closest('.adperf-chip');
    if (chip) {
      e.preventDefault();
      const key = chip.dataset.source;
      const on = SOURCES.filter((s) => state.view.sources[s.key]);
      if (state.view.sources[key] && on.length === 1) return;
      state.view.sources[key] = !state.view.sources[key];
      saveView(state.view);
      renderFromState();
      return;
    }
    const badge = e.target.closest('.adperf-badge');
    if (badge) {
      advertisingLeads({ ...state.range, status: 'NEW', source: drillSource(state.view.sources) });
    }
  });

  // Disclosure: each section remembers its own state; "Expand all" opens every one and
  // closing any section while expanded-all drops back to per-section memory.
  document.querySelectorAll('details.adperf-sec').forEach((d) => {
    d.addEventListener('toggle', () => {
      const key = d.dataset.sec;
      if (!SECTIONS.includes(key) || !state.view) return;
      state.view.open[key] = d.open;
      if (!d.open && state.view.expandAll) state.view.expandAll = false;
      saveView(state.view);
      syncExpandButton();
    });
  });
  document.getElementById('adperf-expand')?.addEventListener('click', () => {
    state.view.expandAll = !state.view.expandAll;
    if (state.view.expandAll) for (const k of SECTIONS) state.view.open[k] = true;
    saveView(state.view);
    applyOpenState();
  });

  // Campaign drill-through, delegated on the stable container (the table re-renders on
  // every chip or period change). A magnet row searches its UTM campaign; call and LSA
  // rows carry their source, since their campaign lives in tags the search does not read.
  document.getElementById('adperf-sources')?.addEventListener('click', (e) => {
    const row = e.target.closest('tr[data-campaign]');
    if (!row || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const kind = row.dataset.kind || 'magnet';
    const src = SOURCES.find((s) => s.key === kind);
    advertisingLeads({ ...state.range, ...(kind === 'magnet' ? { search: row.dataset.campaign } : {}), source: src?.leadSource || null });
    if (e.target.closest('a')) return;
    window.location.hash = '#/leads';
  });

  document.getElementById('adperf-conv-scope')?.addEventListener('change', (e) => {
    convScope = e.target.value || 'all';
    reloadConversions();
  });

  // LSA lead drill-through: the leads page filtered to Local Services, searching the phone.
  document.getElementById('adperf-lsa-card')?.addEventListener('click', (e) => {
    const row = e.target.closest('tr[data-lsalead]');
    if (!row || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    advertisingLeads({ ...state.range, search: row.dataset.lsalead, source: 'Google LSA' });
    if (e.target.closest('a')) return;
    window.location.hash = '#/leads';
  });

  applyOpenState();
  await load();
}

function currentWindow() {
  const now = new Date();
  const to = ymd(now);
  const from = ymd(new Date(now.getTime() - (periodDays - 1) * 86400000));
  return { from, to };
}

async function load() {
  const loadingTarget = document.getElementById('adperf-body');
  const finishRefresh = beginRefresh(loadingTarget, 'Updating advertising results');
  const base = apiBase();
  const body = document.getElementById('adperf-body');
  const rangeEl = document.getElementById('adperf-range');
  const { from, to } = currentWindow();
  if (rangeEl) rangeEl.textContent = `${from} → ${to}`;
  state.range = { from, to };
  state.hoverRow = null;

  let overlay, accounts = [], sources = null, intg = null, conv = null, lsa = null;
  try {
    const [oRes, aRes, sRes, intgRes, cRes, lsaRes] = await Promise.all([
      guardedFetch(`${base}/m5t/v5/coreAdMetrics/overlay?from=${from}&to=${to}&compare=1`),
      guardedFetch(`${base}/m5t/v5/coreAdAccount`),
      guardedFetch(`${base}/m5t/v5/coreAdMetrics/by-source?from=${from}&to=${to}`),
      fetchIntegrations().catch(() => null), // notation only — never blocks the page
      guardedFetch(`${base}/m5t/v5/coreAdMetrics/conversions?from=${from}&to=${to}${convActionsParam()}`).then((r) => r.json()).catch(() => null),
      guardedFetch(`${base}/m5t/v5/coreAdMetrics/lsa?from=${from}&to=${to}`).then((r) => r.json()).catch(() => null),
    ]);
    failIfNotOk(oRes);
    overlay = await oRes.json();
    const aJson = await aRes.json().catch(() => ({}));
    accounts = (aJson?.data || []).filter((a) => a.status !== 'DELETED');
    sources = await sRes.json().catch(() => null);
    intg = intgRes;
    conv = cRes;
    lsa = lsaRes;
  } catch (err) {
    failLoadingStates(loadingTarget?.closest('.page-shell'));
    console.warn('[ad-performance] load failed:', err);
    if (body) body.innerHTML = isDenial(err) ? denialState() : emptyState('error', 'Could not load ad performance. Please try again.');
    return;
  } finally {
    finishRefresh();
    settleLoadingValues(loadingTarget?.closest('.page-shell'));
  }
  state.data = { overlay, accounts, sources, intg, conv, lsa };
  renderConnNote(intg, accounts);
  renderConversions(conv, overlay?.totals?.spend ?? null);
  renderLsa(lsa);
  renderCounting();
  renderFromState();
}

/** Everything that depends on the selected sources: the strip, the chart, the campaign table. */
function renderFromState() {
  renderStrip();
  renderChart();
  renderCampaigns();
}

// ── header notation ───────────────────────────────────────────────────────────
function renderConnNote(intg, accounts) {
  const host = document.getElementById('adperf-conn');
  if (!host) return;
  if (!intg || intg.denied) { host.innerHTML = ''; return; }
  const p = PROVIDERS.google_ads;
  const conn = connFor(intg.providers, 'google_ads');
  const status = statusOf(p, conn, !!intg.configured?.google_ads);
  const manage = canManage()
    ? ` <a href="#/settings/integrations/${p.urlSlug}">Manage</a>` : '';
  if (status === 'connected' || status === 'broken') {
    ensureIntgStyles();
    host.innerHTML = `<span>Google Ads</span> ${statusPill(status, conn)}${manage}`;
    return;
  }
  if (accounts.length) {
    host.innerHTML = `<span>Google Ads</span> <span>${accounts.length} associated account${accounts.length === 1 ? '' : 's'}</span>`;
    return;
  }
  host.innerHTML = '';
}

// ── KPI strip ─────────────────────────────────────────────────────────────────
function setText(id, v) { const el = document.getElementById(id); if (el) el.textContent = v; }
function setHTML(id, v) { const el = document.getElementById(id); if (el) el.innerHTML = v; }
function setTrend(id, t) {
  const el = document.getElementById(id);
  if (!el) return;
  if (!t) { el.className = 'stat-trend'; el.innerHTML = ''; return; }
  el.className = `stat-trend ${t.dir}`;
  el.innerHTML = `<span class="material-symbols-outlined" aria-hidden="true">${t.icon}</span>${esc(t.text)}`;
}

/**
 * The strip reads the window totals, or one day while the chart is being read. Trends
 * compare the window with the previous one of equal length and are hidden in day mode.
 */
function renderStrip(dayRow = state.hoverRow) {
  const grid = document.getElementById('adperf-stats');
  if (!grid || !state.view) return;
  const overlay = state.data?.overlay;
  const sources = state.view.sources;
  const reading = dayRow ? stripFigures(dayRow, sources) : stripFigures(overlay?.totals, sources);
  const prev = !dayRow && overlay?.previous?.totals ? stripFigures(overlay.previous.totals, sources) : null;
  grid.classList.toggle('adperf-strip--day', !!dayRow);
  const dayLabel = dayRow ? `On ${shortDate(dayRow.date)}` : '';

  // Spend
  setText('adperf-spend', fmtMoney(reading.spend));
  setTrend('adperf-spend-trend', prev ? trend(reading.spend, prev.spend) : null);
  const bp = reading.byProvider;
  setText('adperf-spend-sub', dayRow ? dayLabel
    : bp && bp.google_lsa > 0 ? `Google Ads ${fmtMoney(bp.google_ads)} · Local Services ${fmtMoney(bp.google_lsa)}`
      : 'Reported by Google');

  // Leads, with the source chips
  setText('adperf-leads', reading.leads == null ? '—' : `${fmtNum(reading.leads)}${reading.incomplete ? '+' : ''}`);
  setTrend('adperf-leads-trend', prev && !reading.incomplete && !prev.incomplete ? trend(reading.leads, prev.leads) : null);
  const split = reading.split || SOURCES.map((s) => ({ key: s.key, count: 0, on: !!sources[s.key], plural: s.plural, icon: s.icon }));
  setHTML('adperf-leads-sub', dayRow
    ? `${dayLabel} · ${split.filter((s) => s.on).map((s) => `${fmtNum(s.count)} ${s.plural}`).join(' · ')}`
    : `<span class="adperf-chips" role="group" aria-label="Activity sources shown">${split.map((s) =>
      `<button type="button" class="adperf-chip" data-source="${s.key}" aria-pressed="${s.on}" title="${s.on ? 'Hide' : 'Show'} ${s.plural}"><span class="material-symbols-outlined" aria-hidden="true">${s.icon}</span>${s.unavailable ? '—' : fmtNum(s.count) + (s.partial ? '+' : '')} ${s.plural}</button>`).join('')}</span>`);

  // Cost per lead: lower is better; null (dash) when nothing to divide by.
  setText('adperf-cpl', fmtMoney(reading.costPerLead));
  setTrend('adperf-cpl-trend', prev ? trend(reading.costPerLead, prev.costPerLead, { lowerIsBetter: true }) : null);
  const allOn = SOURCES.every((s) => sources[s.key]);
  setText('adperf-cpl-sub', reading.costPerLead == null ? (reading.incomplete ? 'Waiting for complete Local Services totals' : reading.spend ? 'No inquiries or calls in this range' : 'No spend in this range')
    : dayRow ? dayLabel : allOn ? 'Across the selected inquiries and calls' : 'Across the selected sources');

  // Answer rate (window only: outcomes are not per day) and the attention badge.
  const calls = reading.calls;
  setText('adperf-answer', calls?.answer_rate == null ? '—' : `${calls.answer_rate}%`);
  const awaiting = reading.awaiting || 0;
  const badge = awaiting > 0 && !dayRow
    ? ` <a class="adperf-badge" href="#/leads"><span class="material-symbols-outlined" aria-hidden="true">phone_callback</span>${fmtNum(awaiting)} marked New</a>` : '';
  setHTML('adperf-answer-sub', (dayRow ? 'Outcomes are counted for the whole range'
    : calls?.known ? `${fmtNum(calls.answered)} answered · ${fmtNum(calls.missed)} missed · Known call outcomes` : 'No call outcomes yet') + badge);
}

// ── chart ─────────────────────────────────────────────────────────────────────
function renderChart() {
  const body = document.getElementById('adperf-body');
  if (!body || !state.data) return;
  const { overlay, accounts } = state.data;
  if (state.chartCleanup) { state.chartCleanup(); state.chartCleanup = null; }
  if (!accounts.length) {
    body.innerHTML = emptyState('link_off', 'No ad account connected yet. Your agency can connect Google Ads or Local Services Ads to see performance here.');
    return;
  }
  if (!overlay?.connected) {
    body.innerHTML = emptyState('cloud_sync', 'Account connected. No ad data for this range yet. Metrics appear here once they sync.');
    return;
  }
  const rows = overlay.rows || [];
  const sources = state.view.sources;
  const allOn = SOURCES.every((s) => sources[s.key]);
  const label = allOn ? 'Inquiries & calls' : 'Selected activity';
  body.innerHTML = dualAxisChartHTML({
    rows,
    xLabel: (r) => String(r.date || '').slice(5),
    left: { value: (r) => r.spend || 0, label: 'Ad Spend', fmt: (v) => `$${fmtAxis(v)}` },
    right: { value: (r) => selectedLeads(r, sources), label },
    ariaLabel: 'Ad spend and incoming activity over time',
    pointLabel: (r) => `${r.date}: ${fmtMoney(r.spend || 0)} spend, ${fmtNum(selectedLeads(r, sources))} inquiries & calls`,
    hitData: (r) => ({ day: r.date }),
  });
  state.chartCleanup = wireChartHover(body, {
    rows,
    tipHTML: (r) => `
      <div class="m5c-tip__title">${esc(String(r.date || ''))}</div>
      <div class="m5c-tip__row"><i class="m5c-sw m5c-sw--1"></i>${fmtMoney(r.spend || 0)} spend</div>
      <div class="m5c-tip__row"><i class="m5c-sw m5c-sw--2"></i>${fmtNum(selectedLeads(r, sources))} inquiries & calls</div>`,
    onShow: (r) => { state.hoverRow = r; renderStrip(r); },
    onHide: () => { state.hoverRow = null; renderStrip(null); },
  });
}

// ── sections ──────────────────────────────────────────────────────────────────
function setSummary(key, summary) {
  const el = document.getElementById(`adperf-sum-${key}`);
  if (!el) return;
  if (!summary) { el.textContent = ''; el.className = 'adperf-sec-line'; return; }
  el.className = `adperf-sec-line ${summary.tone || ''}`;
  el.innerHTML = esc(summary.text) + (summary.badge
    ? ` <span class="adperf-badge"><span class="material-symbols-outlined" aria-hidden="true">flag_circle</span>${fmtNum(summary.badge)} to review</span>` : '');
}
function applyOpenState() {
  if (!state.view) return;
  document.querySelectorAll('details.adperf-sec').forEach((d) => {
    const key = d.dataset.sec;
    d.open = !!(state.view.expandAll || state.view.open[key]);
  });
  syncExpandButton();
}
function syncExpandButton() {
  const btn = document.getElementById('adperf-expand');
  if (!btn || !state.view) return;
  const visible = [...document.querySelectorAll('details.adperf-sec')].filter((d) => !d.hidden);
  const allOpen = visible.length > 0 && visible.every((d) => d.open);
  btn.textContent = allOpen ? 'Collapse all' : 'Expand all';
  if (allOpen !== state.view.expandAll) { state.view.expandAll = allOpen; saveView(state.view); }
}

function renderCampaigns() {
  const host = document.getElementById('adperf-sources');
  if (!host || !state.view) return;
  const data = state.data?.sources;
  // Older API responses carry no `kind`; those rows are magnet UTM rows.
  const rows = filterSourceRows(data?.rows, state.view.sources).map((r) => ({ ...r, kind: r.kind || 'magnet' }));
  setSummary('campaigns', campaignSummary(data?.rows, state.view.sources, data?.lsa_status));
  if (!rows.length) {
    host.innerHTML = emptyState('search_off', 'No activity in this range yet.');
    return;
  }
  const label = (r) => {
    if (r.campaign && r.campaign !== '(none)') return esc(r.campaign);
    if (r.kind === 'ad_call') return '<span class="adperf-src-untracked">Call ads, campaign not reported</span>';
    if (r.kind === 'lsa') return '<span class="adperf-src-untracked">Local Services, no category</span>';
    if (r.source && r.source !== '(direct)') return esc(r.source);
    return '<span class="adperf-src-untracked">Untracked / direct</span>';
  };
  // What the row drills to. A magnet row needs a real campaign or source to search for;
  // call and LSA rows always have their source, so they always drill.
  const drillable = (r) => r.kind !== 'magnet' || (r.campaign && r.campaign !== '(none)') || (r.source && r.source !== '(direct)');
  const searchFor = (r) => (r.campaign && r.campaign !== '(none)') ? r.campaign : (r.source && r.source !== '(direct)') ? r.source : '';
  const kindCell = (r) => {
    const s = SOURCES.find((x) => x.key === r.kind) || SOURCES[0];
    const name = r.kind === 'ad_call' ? 'Call' : r.kind === 'lsa' ? 'Local Services' : 'Magnet';
    return `<span class="adperf-kind"><span class="material-symbols-outlined" aria-hidden="true">${s.icon}</span>${name}</span>`;
  };
  const anyClicks = rows.some((r) => r.paid_clicks);
  host.innerHTML = `
    <div class="adperf-srcwrap">
      <table class="adperf-srctable">
        <thead><tr><th>Campaign</th><th>Source</th><th>Type</th>${anyClicks ? '<th class="num">Paid clicks</th>' : ''}<th class="num">Inquiries &amp; calls</th></tr></thead>
        <tbody>${rows.map((r) => {
    const can = drillable(r);
    const term = searchFor(r);
    return `
          <tr${can ? ` data-campaign="${esc(term)}" data-kind="${esc(r.kind)}" class="adperf-srcrow--link"` : ''}>
            <td>${can
      ? `<a class="adperf-srclink" href="#/leads" title="View activity" aria-label="View activity: ${esc(term || r.kind)}">${label(r)}<br><small>View activity</small></a>`
      : label(r)}</td>
            <td class="adperf-src-sub">${esc(r.source || '—')}${r.medium ? ` · ${esc(r.medium)}` : ''}</td>
            <td>${kindCell(r)}</td>
            ${anyClicks ? `<td class="num">${r.paid_clicks ? fmtNum(r.paid_clicks) : '<span class="adperf-src-sub">—</span>'}</td>` : ''}
            <td class="num"><b>${fmtNum(r.leads)}</b></td>
          </tr>`;
  }).join('')}
        </tbody>
      </table>
    </div>`;
  const tb = host.querySelector('.adperf-srctable tbody');
  if (tb) capList(tb, { rowSelector: 'tr', noun: 'sources', controlHost: host.querySelector('.adperf-srcwrap') || host });
}

// The ?actions= value for the current scope: 'all' → every action Google
// recorded; 'saved' → no param (server default = the saved selection); an
// action id → that action alone.
function convActionsParam() {
  if (convScope === 'saved') return '';
  return `&actions=${encodeURIComponent(convScope)}`;
}
// Scope change refetches ONLY the conversions slice.
async function reloadConversions() {
  const base = apiBase();
  const { from, to } = currentWindow();
  const conv = await guardedFetch(`${base}/m5t/v5/coreAdMetrics/conversions?from=${from}&to=${to}${convActionsParam()}`)
    .then((r) => r.json()).catch(() => null);
  if (state.data) state.data.conv = conv;
  renderConversions(conv, state.data?.overlay?.totals?.spend ?? null);
}

/**
 * Other conversions: Google Ads conversion actions, a SEPARATE stream from leads (a
 * lead that also fires a gtag conversion would double-count), so the section carries
 * its own provenance in the summary line and never feeds the strip. Hidden only when
 * Google Ads has nothing to scope over at all.
 */
function renderConversions(conv, spendTotal) {
  const card = document.getElementById('adperf-conv-card');
  const bodyEl = document.getElementById('adperf-conv-body');
  if (!card || !bodyEl) return;
  const available = conv?.available || [];
  const savedIds = conv?.saved_ids || [];
  const actions = conv?.actions || [];
  if (!conv?.ok || (!available.length && !savedIds.length && !actions.length)) {
    card.hidden = true; setSummary('conversions', null); syncExpandButton();
    return;
  }
  card.hidden = false;
  const sel = document.getElementById('adperf-conv-scope');
  if (sel) {
    const opts = [{ v: 'all', label: 'All conversion actions' }];
    if (savedIds.length) opts.push({ v: 'saved', label: 'Counted actions' });
    for (const a of available) opts.push({ v: String(a.id), label: a.name || String(a.id) });
    if (!opts.some((o) => o.v === convScope)) convScope = 'all';
    sel.innerHTML = opts.map((o) => `<option value="${esc(o.v)}"${o.v === convScope ? ' selected' : ''}>${esc(o.label)}</option>`).join('');
  }
  setSummary('conversions', conversionSummary(conv));
  const totalAll = Number(conv.totals?.all_conversions ?? conv.totals?.conversions ?? 0);
  // Cost per conversion against the SELECTED actions. Deliberately not beside Cost / Lead:
  // that one divides by our leads, this one by Google's attributed conversions.
  const cpc = (spendTotal != null && totalAll > 0) ? Math.round((Number(spendTotal) / totalAll) * 100) / 100 : null;
  setText('adperf-conv-cpc', cpc == null ? 'Reported by Google Ads' : `${fmtMoney(cpc)} per conversion · Reported by Google Ads`);
  if (!actions.length) {
    bodyEl.innerHTML = emptyState('search_off', 'No conversions in this range for the selected actions.');
    syncExpandButton();
    return;
  }
  const showValue = actions.some((a) => (a.all_value ?? a.value) > 0);
  bodyEl.innerHTML = `
    <div class="adperf-srcwrap">
      <table class="adperf-srctable">
        <thead><tr><th>Conversion action</th><th>Category</th><th class="num">All conv.</th><th class="num">Counted</th>${showValue ? '<th class="num">Value</th>' : ''}</tr></thead>
        <tbody>${actions.map((a) => `
          <tr>
            <td>${esc(a.name || a.id)}</td>
            <td class="adperf-src-sub">${esc(String(a.category || '').toLowerCase().replace(/_/g, ' ') || '—')}</td>
            <td class="num"><b>${fmtNum(a.all_conversions ?? a.conversions)}</b></td>
            <td class="num">${fmtNum(a.conversions)}</td>
            ${showValue ? `<td class="num">${fmtMoney(a.all_value ?? a.value)}</td>` : ''}
          </tr>`).join('')}
        </tbody>
      </table>
    </div>
    <p class="adperf-note">Counted actions are chosen on the Google Ads integration page. All conv. counts every conversion action Google recorded, including actions outside the campaign bidding goals. These are Google's own numbers and may overlap the leads above, so they are never added together.</p>`;
  syncExpandButton();
}

/**
 * Local Services: its own section, never blended into the overlay's spend/lead chart
 * beyond the lead count (LSA leads are our rows; LSA spend is Google's). Hidden until a
 * linked account shows LSA activity. With no leads yet the body is one sentence.
 */
function renderLsa(data) {
  const card = document.getElementById('adperf-lsa-card');
  const bodyEl = document.getElementById('adperf-lsa-body');
  if (!card || !bodyEl) return;
  const summary = lsaSummary(data);
  if (!summary) { const shortcut = document.getElementById('adperf-lsa-shortcut'); if (shortcut) shortcut.hidden = true; card.hidden = true; setSummary('lsa', null); syncExpandButton(); return; }
  card.hidden = false;
  setSummary('lsa', summary);

  const shortcut = document.getElementById('adperf-lsa-shortcut');
  if (shortcut) {
    shortcut.hidden = false;
    shortcut.innerHTML = `<button type="button" class="adperf-lsa-jump">Local Services: ${esc(summary.text)} →</button>`;
    shortcut.querySelector('button').onclick = () => {
      card.open = true;
      card.querySelector('summary').focus();
      card.scrollIntoView({ block: 'start' });
    };
  }
  const t = data.totals || {};
  const leads = data.leads || [];
  const disputes = data.disputes || [];
  const report = data.account_report;
  const hasTotals = report && report.status !== 'unavailable' && report.phone_calls != null;
  const googleLink = '<a href="https://ads.google.com/localservices/leads" target="_blank" rel="noopener">Open Google Local Services →</a>';
  const partialSuffix = report?.status === 'partial' ? '+' : '';
  const aggregateCards = hasTotals ? `<div class="stats-grid" style="margin-bottom:16px">
    ${statCard({ icon: 'call', label: 'Calls received', value: fmtNum(report.phone_calls) + partialSuffix, sub: 'Includes unanswered calls' })}
    ${statCard({ icon: 'phone_in_talk', label: 'Connected calls', value: fmtNum(report.connected_phone_calls) + partialSuffix, sub: 'Connected for over 30 seconds' })}
    ${statCard({ icon: 'paid', label: 'Charged leads', value: fmtNum(report.charged_leads) + partialSuffix, sub: 'Reported by Google' })}
    ${statCard({ icon: 'payments', label: 'LSA spend', value: fmtMoney(report.spend ?? t.spend) + (report.spend != null ? partialSuffix : ''), sub: report.spend != null ? 'Reported by Google Local Services' : 'Google Ads campaign spend' })}
  </div>` : '<p class="adperf-note">Call totals are unavailable. Check Google Local Services for this period.</p>';
  const freshness = report?.status === 'partial' ? `<p class="adperf-note">Showing reported totals for ${fmtNum(report.covered_days)} of ${fmtNum(report.expected_days)} days. Remaining days are awaiting sync.</p>` : report?.status === 'stale' ? '<p class="adperf-note">Update delayed. Showing the last retrieved totals.</p>' : '';
  if (!leads.length) {
    bodyEl.innerHTML = `${aggregateCards}${freshness}<p class="adperf-note">${hasTotals && report.phone_calls > 0 ? 'Google reported calls, but individual lead details are not available here.' : 'No individual lead records are available here.'} ${googleLink}</p>`;
    syncExpandButton();
    return;
  }
  const kpis = `${aggregateCards}${freshness}
    <p class="adperf-note">${fmtNum(t.leads)} lead records available in Magnet. Call totals and lead records are counted separately. ${googleLink}</p>`;
  const table = `
    <div class="adperf-srcwrap">
      <table class="adperf-srctable">
        <thead><tr><th>Lead</th><th>Contact</th><th>Type</th><th>Category</th><th>Google status</th><th class="num">Charged</th><th>Received</th></tr></thead>
        <tbody>${leads.map((l) => {
    const term = l.phone || l.name;
    const when = l.captured_at ? new Date(l.captured_at).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '—';
    return `
          <tr${term ? ` data-lsalead="${esc(term)}" class="adperf-srcrow--link"` : ''}>
            <td>${term ? `<a class="adperf-srclink" href="#/leads" title="See this lead" aria-label="See this lead: ${esc(l.name)}">${esc(l.name)}<br><small>See this lead</small></a>` : esc(l.name)}</td>
            <td class="adperf-src-sub">${esc([l.phone, l.email].filter(Boolean).join(' · ') || '—')}</td>
            <td>${esc(l.type || '—')}${l.missed ? ' <span class="adperf-src-untracked">· missed</span>' : ''}</td>
            <td class="adperf-src-sub">${esc(l.category || '—')}</td>
            <td class="adperf-src-sub">${esc(String(l.lsa_status || '—').toLowerCase())}${l.dispute ? ' <span class="adperf-src-untracked">· dispute candidate</span>' : ''}</td>
            <td class="num">${l.charged ? '<b>yes</b>' : '<span class="adperf-src-sub">no</span>'}</td>
            <td class="adperf-src-sub">${esc(when)}</td>
          </tr>`;
  }).join('')}
        </tbody>
      </table>
    </div>`;
  const disputeBlock = disputes.length ? `
    <div class="card-header" style="padding:16px 0 0">
      <h3 class="card-title" style="font-size:1rem"><span class="material-symbols-outlined" aria-hidden="true">flag_circle</span> Dispute candidates</h3>
      <span class="live-indicator">${fmtNum(disputes.length)} inside the 30 day credit window</span>
    </div>
    <div class="adperf-srcwrap">
      <table class="adperf-srctable">
        <thead><tr><th>Lead</th><th>Why</th><th class="num">Days left</th></tr></thead>
        <tbody>${disputes.map((d) => `
          <tr>
            <td>${esc(d.name)}${d.phone ? ` <span class="adperf-src-sub">${esc(d.phone)}</span>` : ''}</td>
            <td class="adperf-src-sub">${esc(d.reason)}</td>
            <td class="num"><b>${fmtNum(d.days_left)}</b></td>
          </tr>`).join('')}
        </tbody>
      </table>
    </div>
    <p class="adperf-note">These charged leads look credit-eligible. Review and dispute them in <a href="https://ads.google.com/localservices/leads" target="_blank" rel="noopener">your Google Local Services lead inbox</a> before the window closes.</p>` : '';
  bodyEl.innerHTML = `
    ${kpis}
    ${table}
    ${disputeBlock}
    <p class="adperf-note">These available lead records are also in your Leads inbox. They may not include every call Google reports.</p>`;
  const tb = bodyEl.querySelector('.adperf-srctable tbody');
  if (tb) capList(tb, { rowSelector: 'tr', noun: 'leads', controlHost: bodyEl.querySelector('.adperf-srcwrap') || bodyEl });
  syncExpandButton();
}

/** The detail reader's block: counting rules and where the data came from. */
function renderCounting() {
  const host = document.getElementById('adperf-counting');
  if (!host) return;
  const accounts = state.data?.accounts || [];
  setSummary('counting', { text: 'Definitions and data provenance', tone: '' });
  const synced = accounts
    .map((a) => ({ cid: a.external_customer_id, login: a.login_customer_id, tz: a.last_synced?.tz, name: a.display_name, status: a.sync_status }))
    .filter((a) => a.cid);
  const provenance = synced.length
    ? `<ul>${synced.map((a) => `<li>${esc(a.name || 'Google Ads')} · customer ID ${esc(a.cid)}${a.login ? ` under manager ${esc(a.login)}` : ''} · ${a.tz ? `last synced ${esc(new Date(a.tz).toLocaleString())}` : 'not synced yet'}${a.status === 'ERROR' ? ' · <b>last sync failed</b>' : ''}</li>`).join('')}</ul>`
    : '<p class="adperf-src-sub">No ad account is linked to this workspace.</p>';
  host.innerHTML = `
    <dl class="adperf-defs">
      <dt>Inquiries & calls</dt>
      <dd>Website inquiries, Google Ads call records, and Google Local Services activity. Local Services call totals replace its individual phone records for the same day; messages and bookings are added separately. These are interactions, not unique people or confirmed customers. A plus sign means a source is incomplete.</dd>
      <dt>Cost per inquiry or call</dt>
      <dd>Total ad spend divided by the selected activity. Changing source filters changes the activity counted, not the spend. The figure is unavailable when Local Services totals are incomplete. Local Services campaign spend is counted once, even when it is also in the Google Ads account total.</dd>
      <dt>Answer rate</dt>
      <dd>Answered calls as a share of calls whose outcome Google reported. Calls with no reported outcome are left out rather than counted either way. Connected Local Services calls mean over 30 seconds and do not change this answer rate. Marked New reflects the saved status, not whether someone has followed up.</dd>
      <dt>Other conversions</dt>
      <dd>Google's own conversion counts for the actions you track. A lead that also fires a conversion appears in both places, which is why these are never added to leads from ads.</dd>
      <dt>Previous period</dt>
      <dd>Trend chips compare this range with the equal-length range immediately before it, and are hidden when there is nothing to compare against.</dd>
      <dt>Data source</dt>
      <dd>${provenance}Syncs run daily; the integration page can run one now.</dd>
    </dl>`;
}

export default { render, init };
